import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import EDateText from '@/components/everridge/EDateText';
import type { IntegrationMessageKind, IntegrationMessageListItemDto } from './integration-message.types';
import { hasAggregateFields, hasExternalIdField, hasTenantField, hasTryCountField } from './integration-message.utils';

export const useIntegrationMessagesColumns = (kind: IntegrationMessageKind) => {
    const { t } = useTranslation();

    const columns: ColumnDef<IntegrationMessageListItemDto>[] = [
        {
            id: 'select',
            header: ({ table }) => (
                <div className="flex items-center justify-center">
                    <Checkbox
                        checked={table.getIsAllPageRowsSelected()}
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'type',
            header: t('integrationMessages.table.type'),
            cell: ({ row }) => <span className="text-sm">{row.original.type}</span>,
        },
    ];

    if (hasTenantField(kind)) {
        columns.push({
            accessorKey: 'tenantName',
            header: t('integrationMessages.table.tenant'),
            cell: ({ row }) => (
                <div className="text-sm">
                    <div>{row.original.tenantName ?? '—'}</div>
                    <div className="text-xs text-gray">{row.original.tenantId}</div>
                </div>
            ),
        });
    }

    if (hasAggregateFields(kind)) {
        columns.push({
            accessorKey: 'aggregateId',
            header: t('integrationMessages.table.aggregateId'),
            cell: ({ row }) => <span className="text-sm">{row.original.aggregateId ?? '—'}</span>,
        });
    }

    if (hasExternalIdField(kind)) {
        columns.push({
            accessorKey: 'externalId',
            header: t('integrationMessages.table.externalId'),
            cell: ({ row }) => <span className="text-sm">{row.original.externalId ?? '—'}</span>,
        });
    }

    columns.push(
        {
            accessorKey: 'isProcessed',
            header: t('integrationMessages.table.processed'),
            cell: ({ row }) => (
                <span className="text-sm">
                    {row.original.isProcessed
                        ? t('integrationMessages.filters.processedYes')
                        : t('integrationMessages.filters.processedNo')}
                </span>
            ),
        },
        {
            id: 'timestamp',
            header:
                kind === 'inbox'
                    ? t('integrationMessages.table.receivedAt')
                    : t('integrationMessages.table.occurredAt'),
            cell: ({ row }) => (
                <EDateText
                    date={new Date(row.original.receivedAt ?? row.original.occurredAt ?? '')}
                />
            ),
        },
        {
            accessorKey: 'error',
            header: t('integrationMessages.table.error'),
            cell: ({ row }) => (
                <span className="line-clamp-2 max-w-xs text-sm text-destructive">
                    {row.original.error ?? '—'}
                </span>
            ),
        },
    );

    if (hasTryCountField(kind)) {
        columns.splice(columns.length - 2, 0, {
            accessorKey: 'tryCount',
            header: t('integrationMessages.table.tryCount'),
            cell: ({ row }) => <span className="text-sm">{row.original.tryCount ?? 0}</span>,
        });
    }

    return columns;
};
